import { useEffect } from 'react'
import type { JSX } from 'react'

import { clsx } from 'clsx'

import {
  Button,
  CircleCheckBigIcon,
  Flex,
  IconButton,
} from '@buffer-mono/popcorn'

import { graphql } from '~publish/graphql'
import type { FragmentOf } from '~publish/graphql'
import { canPerform } from '~publish/utils/permissions/canPerform'

import { useCommentData } from '../context/CommentContext'
import { VIEW_ONLY_COMMENT_TOOLTIP } from '../../shared/viewOnlyCopy'
import { useDismissComment } from '../../hooks/comments/useDismissComment'
import { useUndismissComment } from '../../hooks/comments/useUndismissComment'
import { triggerEngagementScoreStart } from '../CommentsList/utils/commentScoreSessionManager'

import styles from './CommentActions.module.css'

export const CommentActions_Channel = graphql(/* GraphQL */ `
  fragment CommentActions_Channel on Channel @_unmask {
    id
    allowedActions
  }
`)

type CommentActionsProps = {
  channel: FragmentOf<typeof CommentActions_Channel>
  onLoadingChange?: (isLoading: boolean) => void
  disabled?: boolean
  expanded?: boolean
  isHovered?: boolean
  onExpandNextComment?: (
    fromCommentId?: string,
    onlyRepliable?: boolean,
  ) => void
  onCollapse?: () => void
  isLocallyDismissed?: boolean
  isLocallyReplied?: boolean
  isInterimExpanded?: boolean
  onDismissed?: () => void
  onUndismissed?: () => void
}

export const CommentActions = ({
  channel,
  onLoadingChange,
  disabled = false,
  expanded = false,
  isHovered = false,
  onExpandNextComment,
  onCollapse,
  isLocallyDismissed = false,
  isLocallyReplied = false,
  isInterimExpanded = false,
  onDismissed,
  onUndismissed,
}: CommentActionsProps): JSX.Element | null => {
  const comment = useCommentData()
  const { dismissComment, loading: isDismissingComment } = useDismissComment()
  const { undismissComment, loading: isUndismissingComment } =
    useUndismissComment()

  const isLoading = isDismissingComment || isUndismissingComment
  const canManage = canPerform(channel, 'manageComments')

  // Notify parent of loading state changes
  useEffect(() => {
    onLoadingChange?.(isLoading)
  }, [isLoading, onLoadingChange])

  const handleDismissComment = async (): Promise<void> => {
    // Trigger score tracking BEFORE the mutation so score is still available
    triggerEngagementScoreStart(channel.id)
    const success = await dismissComment(comment.id)

    // Only notify parent and navigate if dismiss succeeded
    if (success) {
      onDismissed?.()

      // Only expand next comment if this comment was expanded
      if (expanded) {
        onCollapse?.()
        onExpandNextComment?.()
      }
    }
  }

  const handleUndoDismissComment = async (): Promise<void> => {
    // Trigger score tracking BEFORE the mutation so score is still available
    triggerEngagementScoreStart(channel.id)
    const success = await undismissComment(comment.id)

    // Only notify parent if undismiss succeeded
    if (success) {
      onUndismissed?.()
    }
  }

  // View-only actors see the buttons in a disabled state with an explanatory
  // tooltip. `aria-disabled` (not native `disabled`) keeps the button hoverable
  // and focusable — Popcorn's disabled buttons get `pointer-events: none`,
  // which would suppress the tooltip. Clicks below no-op when view-only.
  const isViewOnly = !canManage
  const finalDisabled = disabled || isLoading
  const viewOnlyTooltip = isViewOnly ? VIEW_ONLY_COMMENT_TOOLTIP : null

  // Determine visibility based on comment status and state
  const shouldShowActions = (): boolean => {
    // Replied comments (server or local): show on hover or when expanded
    if (comment.status === 'replied' || isLocallyReplied) {
      return isHovered || expanded
    }

    // Resolved (dismissed): always show actions
    if (comment.status === 'dismissed' || isLocallyDismissed) {
      return true
    }

    // New/unreplied comments: show on hover or when expanded
    if (comment.status === 'unreplied' || comment.status === 'new') {
      return isHovered || expanded
    }

    return false
  }

  const isDismissed = comment.status === 'dismissed' || isLocallyDismissed
  const canResolve =
    (comment.status === 'unreplied' || comment.status === 'new') &&
    !isLocallyDismissed &&
    !isLocallyReplied

  // View-only actors always see the disabled action so the affordance is
  // explicit, rather than the buttons only appearing on hover.
  const isVisible = isViewOnly || shouldShowActions()

  return (
    <CommentActionsView
      mode={isDismissed ? 'reopen' : canResolve ? 'resolve' : null}
      visible={isVisible}
      viewOnly={isViewOnly}
      disabled={finalDisabled}
      viewOnlyTooltip={viewOnlyTooltip}
      expanded={expanded}
      isInterimExpanded={isInterimExpanded}
      onResolve={(): void => {
        void handleDismissComment()
      }}
      onReopen={(): void => {
        void handleUndoDismissComment()
      }}
    />
  )
}

type CommentActionsViewProps = {
  mode: 'resolve' | 'reopen' | null
  visible: boolean
  viewOnly: boolean
  disabled: boolean
  viewOnlyTooltip: string | null
  expanded: boolean
  isInterimExpanded: boolean
  onResolve: () => void
  onReopen: () => void
}

export const CommentActionsView = ({
  mode,
  visible,
  viewOnly,
  disabled,
  viewOnlyTooltip,
  expanded,
  isInterimExpanded,
  onResolve,
  onReopen,
}: CommentActionsViewProps): JSX.Element => {
  return (
    <Flex
      direction="column"
      gap="xs"
      className={visible ? styles.visible : styles.hidden}
    >
      {mode === 'reopen' && (
        <>
          <IconButton
            label="Reopen this comment"
            onClick={() => {
              if (viewOnly) return
              onReopen()
            }}
            tooltip={viewOnlyTooltip ?? 'Undo resolve'}
            variant={isInterimExpanded || expanded ? 'secondary' : 'tertiary'}
            className={clsx(
              isInterimExpanded
                ? styles.dismissButton
                : styles.undoDismissButton,
              styles.desktopOnly,
              viewOnly && styles.viewOnlyDisabled,
            )}
            disabled={disabled}
            aria-disabled={viewOnly || undefined}
            aria-label="Reopen this comment"
            data-expanded={expanded ? 'true' : 'false'}
          >
            <CircleCheckBigIcon />
          </IconButton>
          <Button
            onClick={() => {
              if (viewOnly) return
              onReopen()
            }}
            variant="secondary"
            className={clsx(
              styles.mobileOnly,
              viewOnly && styles.viewOnlyDisabled,
            )}
            disabled={disabled}
            aria-disabled={viewOnly || undefined}
          >
            <CircleCheckBigIcon />
            Undo resolve
          </Button>
        </>
      )}
      {mode === 'resolve' && (
        <>
          <IconButton
            label="Resolve this comment"
            onClick={() => {
              if (viewOnly) return
              onResolve()
            }}
            tooltip={
              viewOnlyTooltip ??
              "Resolve this comment if you don't want to reply to it anymore."
            }
            variant="secondary"
            className={clsx(
              styles.dismissButton,
              styles.desktopOnly,
              viewOnly && styles.viewOnlyDisabled,
            )}
            disabled={disabled}
            aria-disabled={viewOnly || undefined}
            name="dismiss-comment"
            aria-label="Resolve this comment"
          >
            <CircleCheckBigIcon />
          </IconButton>
          <Button
            onClick={() => {
              if (viewOnly) return
              onResolve()
            }}
            variant="secondary"
            className={clsx(
              styles.mobileOnly,
              viewOnly && styles.viewOnlyDisabled,
            )}
            disabled={disabled}
            aria-disabled={viewOnly || undefined}
          >
            <CircleCheckBigIcon />
            Resolve
          </Button>
        </>
      )}
      {/* TODO: Add save comment functionality */}
      {/*
        {(comment.status === 'replied' || isLocallyReplied) &&
          onSaveComment && (
          <IconButton
            label="Save this comment"
            onClick={onSaveComment}
            tooltip="Save this comment"
            aria-label="Save this comment"
            variant="secondary"
          >
            <BookmarkIcon />
          </IconButton>
        )}*/}
    </Flex>
  )
}
